import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, ShoppingBag, Menu, X, Loader2 } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useSite } from '../../context/SiteContext';
import { navigation } from '../../data/mock';
import { productsApi } from '../../services/api';
import CartDrawer from '../cart/CartDrawer';

const Header = () => {
  const { cartCount, setIsCartOpen } = useCart();
  const { siteData } = useSite();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  // Fetch all products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await productsApi.getAll();
        setAllProducts(response.data);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      }
    };
    fetchProducts();
  }, []);

  // Filter products based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const query = searchQuery.toLowerCase();
    const filtered = allProducts.filter(product => 
      product.name?.toLowerCase().includes(query) ||
      product.description?.toLowerCase().includes(query) ||
      product.category?.toLowerCase().includes(query) ||
      product.shortDescription?.toLowerCase().includes(query)
    );
    setSearchResults(filtered);
    setIsSearching(false);
  }, [searchQuery, allProducts]);

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  // Handle clicking on a search result
  const handleResultClick = (slug) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(`/products/${slug}`);
  };

  // Close search on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <>
      {/* Announcement Bar */}
      <div className="bg-[#2d4a47] text-white text-center py-2.5 text-sm tracking-wider font-light">
        {siteData.config.tagline}
      </div>

      {/* Main Header */}
      <header className="bg-[#FFFFFE] border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <div className="flex justify-center py-4">
            <Link to="/" className="text-center">
              <img 
                src="https://res.cloudinary.com/dglumvmjs/image/upload/f_auto,q_auto/v1768977323/eden-remedies/eden-logo.png" 
                alt="Eden Remedies natural and organic skincare" 
                className="h-16 md:h-20 w-auto"
                width={290}
                height={112}
              />
            </Link>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between pb-4">
            {/* Left - Search */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 hover:text-[#2d4a47] transition-colors flex items-center gap-2 text-sm tracking-wider"
              >
                <Search size={18} />
                <span className="hidden sm:inline">Search</span>
              </button>
            </div>

            {/* Center - Navigation Links */}
            <nav className="hidden md:flex items-center gap-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-sm tracking-wider text-gray-800 hover:text-[#2d4a47] transition-colors font-medium"
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Right - Icons */}
            <div className="flex items-center gap-4">
              <Link to="/account" className="p-2 hover:text-[#2d4a47] transition-colors" aria-label="My Account">
                <User size={20} />
              </Link>
              <button
                onClick={() => setIsCartOpen(true)}
                className="p-2 hover:text-[#2d4a47] transition-colors relative"
                aria-label="Shopping Cart"
              >
                <ShoppingBag size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#2d4a47] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="p-2 hover:text-[#2d4a47] transition-colors md:hidden"
                aria-label="Open Menu"
              >
                <Menu size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        {searchOpen && (
          <div className="border-t border-gray-100 py-4 px-4 relative">
            <div className="max-w-xl mx-auto">
              <form onSubmit={handleSearchSubmit}>
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search products..."
                    className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-none focus:outline-none focus:border-[#2d4a47]"
                  />
                  <button 
                    type="submit"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#2d4a47]"
                  >
                    {isSearching ? <Loader2 size={20} className="animate-spin" /> : <Search size={20} />}
                  </button>
                </div>
              </form>
              
              {/* Search Results Dropdown */}
              {searchQuery.trim() !== '' && (
                <div className="absolute left-0 right-0 mx-4 mt-2 bg-white border border-gray-200 shadow-lg max-h-96 overflow-y-auto z-50">
                  <div className="max-w-xl mx-auto">
                    {searchResults.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        No products found for "{searchQuery}"
                      </div>
                    ) : (
                      <>
                        <div className="p-3 border-b border-gray-100 text-sm text-gray-500">
                          {searchResults.length} product{searchResults.length !== 1 ? 's' : ''} found
                        </div>
                        {searchResults.map((product) => (
                          <button
                            key={product.id}
                            onClick={() => handleResultClick(product.slug)}
                            className="w-full flex items-center gap-4 p-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 last:border-0"
                          >
                            <img
                              src={product.images?.[0] || product.imageUrl || 'https://via.placeholder.com/56'}
                              alt="Natural and organic skincare product"
                              className="w-14 h-14 object-cover rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 truncate">{product.name}</h4>
                              <p className="text-sm text-gray-500">{product.category}</p>
                              <p className="text-sm font-semibold text-[#2d4a47]">
                                ${product.variants?.[0]?.price?.toFixed(2) || product.price?.toFixed(2)}
                              </p>
                            </div>
                          </button>
                        ))}
                        {searchResults.length > 0 && (
                          <button
                            onClick={handleSearchSubmit}
                            className="w-full p-3 text-center text-sm text-[#2d4a47] hover:bg-[#f5f1eb] font-medium transition-colors"
                          >
                            View all results for "{searchQuery}"
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-white">
          <div className="p-4 flex justify-end">
            <button onClick={() => setMobileMenuOpen(false)}>
              <X size={24} />
            </button>
          </div>
          <nav className="flex flex-col items-center gap-6 pt-10">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-lg tracking-wider text-gray-800 hover:text-[#2d4a47]"
              >
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      )}

      <CartDrawer />
    </>
  );
};

export default Header;
